// src/prototype/pages/EAOpportunitiesPage.tsx
// ============================================================
// EA STEP 2: EVALUATE & SHORTLIST
// "Which opportunities are credible and relevant enough to carry forward?"
// 
// UPDATED: Uses useDemoPlan hook for Pro/Freemium gating
// UPDATED: "Adjust Weights →" moved to banner (removed separate band)
// UPDATED: Now imports from shared mockOpportunities.ts for consistency
//          with EAValidationPage - banner values will match!
// 
// DESIGN PRINCIPLE: DECISION MODE
// - Compact ExplorerRow list (no inline expand). Analytical depth lives on the brief / ROI model.
// - API-backed ranking, filters, pagination, and portfolio selection stay on this branch.
//
// KEY: Uses EAContext for finalistIds and categoryWeights so selections persist to Step 3
// ============================================================

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  MessageCircle,
  X,
  Zap,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  AlertTriangle,
} from 'lucide-react';
import { EAJourneyBar } from '../components/EAJourneyBar';
import { OIRReportView } from '../components/oir/report/OIRReportView';
import { type SortOption } from '../components/oir/OIRSortBar';
import { EATabBar, type EvaluateTab, resolveEvaluateTab } from '../components/EATabBar';
import { useEA, type InitiativeShortlistBucket } from '../contexts/EAContext';
import { ExplorerRow, type ExplorerRowBrief } from '../components/ExplorerRow';
import { CompareTray } from '../components/CompareTray';
import { ExplorerCompareTable } from '../components/ExplorerCompareTable';
import { useCompareSelection, MIN_COMPARE } from '../hooks/useCompareSelection';
import type { EffortTag } from '../components/EffortTagPill';
import { verdictFromScore } from '../theme/decisionTokens';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import {
  hasCorePersonalizationComplete,
  getPersonalizationBlockers,
  PERSONALIZATION_TAB_MIN_PERCENT,
  initiativesScoreFromFinalistCount,
} from '../utils/personalizationProgress';
import { useEAPersonalizationProgress } from '../hooks/useEAPersonalizationProgress';
import { useAuth } from '@/contexts/AuthContext';
import { EA_CLIENT_ID } from '../constants/eaClient';
import {
  fetchPortfolioIntelligenceReport,
  mapReportToOpportunities,
  type PIReport,
  type ReportOpportunity,
} from '../services/portfolioIntelligenceService';
import type { ApiRoadmapBuckets } from '../components/oir/report/OIRSectionExecutionRoadmap';
import { EAApiErrorPanel } from '../components/EAApiErrorPanel';
import {
  fetchOpportunities,
  fetchOpportunity,
  fetchEvaluation,
  getSelection,
  peekOpportunities,
  selectUseCase,
  deselectUseCase,
  UCE_CATALOG_FILTERS,
  type UCEOpportunity,
} from '../services/useCaseExplorerService';

const FINALIST_SNAPSHOTS_KEY = 'ea_finalist_snapshots';
/** DMD-1064: Organization filter persists for the browser session. */
const ORG_FILTER_SESSION_KEY = 'ea_uce_org_filter';
const ORG_FILTER_ALL = 'all';

type OrgOption = { id: string; name: string };

function readOrgFilterSession(): string {
  try {
    const raw = sessionStorage.getItem(ORG_FILTER_SESSION_KEY);
    return raw && raw.trim() ? raw.trim() : ORG_FILTER_ALL;
  } catch {
    return ORG_FILTER_ALL;
  }
}

function writeOrgFilterSession(value: string): void {
  try {
    if (value === ORG_FILTER_ALL) sessionStorage.removeItem(ORG_FILTER_SESSION_KEY);
    else sessionStorage.setItem(ORG_FILTER_SESSION_KEY, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function normalizeClientList(payload: unknown): OrgOption[] {
  const rows = Array.isArray(payload)
    ? payload
    : (payload && typeof payload === 'object' && Array.isArray((payload as { data?: unknown }).data)
      ? (payload as { data: unknown[] }).data
      : []);
  const seen = new Set<string>();
  const out: OrgOption[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const r = row as Record<string, unknown>;
    const id = String(r.client_id ?? r.id ?? '').trim();
    const name = String(r.client_name ?? r.name ?? '').trim();
    if (!id || !name || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, name });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Persist lightweight opportunity cards so Executive Summary can show real selections
 * even when the explorer page (paginated) or PI decision canvas does not include them. */
function loadFinalistSnapshots(): Record<string, ReportOpportunity> {
  try {
    const raw = localStorage.getItem(FINALIST_SNAPSHOTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveFinalistSnapshots(snapshots: Record<string, ReportOpportunity>) {
  try {
    localStorage.setItem(FINALIST_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch (e) {
    console.warn('[EA] Failed to save finalist snapshots:', e);
  }
}

function toReportOpportunitySnapshot(opp: {
  id: string;
  rank?: number;
  title: string;
  category: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; base?: number };
  confidence?: string | number;
  valueBand?: string;
}): ReportOpportunity {
  return {
    id: String(opp.id),
    rank: opp.rank ?? 0,
    title: opp.title,
    category: opp.category,
    implementationEffort: opp.implementationEffort,
    timeToValue: opp.timeToValue,
    roiEstimate: {
      low: opp.roiEstimate.low,
      high: opp.roiEstimate.high,
      base: opp.roiEstimate.base,
    },
    confidence: String(opp.confidence ?? 'Medium'),
    ...(opp.valueBand ? { valueBand: opp.valueBand } : {}),
  };
}

/** Convert ROI-model annual benefit dollars (or already-$M) to millions for display. */
function dollarsOrMToMillions(raw: number | null | undefined): number {
  if (raw == null || !Number.isFinite(raw) || raw <= 0) return 0;
  return raw > 1_000 ? raw / 1_000_000 : raw;
}

/** DMD-1063: human-readable "last computed" label for portfolio metrics. */
import { formatLastComputedLabel as formatPortfolioLastComputedLabel } from '../utils/formatLastComputed';

function uceToReportSnapshot(uce: UCEOpportunity, idx = 0): ReportOpportunity {
  const baseM = dollarsOrMToMillions(uce.annual_benefit_base ?? uce.financial_composite);
  const consM = dollarsOrMToMillions(uce.annual_benefit_conservative) || baseM;
  const aggrM = dollarsOrMToMillions(uce.annual_benefit_aggressive) || baseM;
  const roiLow = Math.min(consM || baseM, aggrM || baseM);
  const roiHigh = Math.max(consM || baseM, aggrM || baseM);
  const valueBand = String(uce.value_band ?? '').trim() || undefined;
  return {
    id: String(uce.use_case_id),
    rank: uce.rank ?? idx + 1,
    title: uce.name,
    category: uce.categoryFit?.trim() || uce.category_name || 'General',
    implementationEffort: effortTierFromImplementationScore(
      uce.implementation_score ?? uce.ease_of_implementation_score,
    ),
    timeToValue: uce.timeline,
    roiEstimate: { low: roiLow, high: roiHigh, base: baseM || undefined },
    confidence: String(uce.value_band ?? 'Medium'),
    ...(valueBand ? { valueBand } : {}),
  };
}
// ============================================================
// ROI FORMATTING UTILITIES
// Handles both legacy mock data (values in millions like 7.9)
// and real BigQuery data (values in raw dollars like 7887250)
// ============================================================

/** Money / ROI display — always 2 decimal places to avoid long float noise from API or math. */
const formatRoiValue = (value: number): string => {
  if (value < 100) {
    return value.toFixed(2);
  }
  return (value / 1_000_000).toFixed(2);
};

/** Scores and other small numeric fields from the API (may be long-float). */
const formatNumber2 = (n: number): string =>
  (Number.isFinite(n) ? n : 0).toFixed(2);

const formatRoiRange = (low: number, high: number, unit?: string): string => {
  const formattedLow = formatRoiValue(low);
  const formattedHigh = formatRoiValue(high);
  // Guard: only allow valid unit symbols — reject strings like "annual"
  const suffix = (unit && /^[MBK%]$/i.test(unit)) ? unit : 'M';
  if (Math.abs(low - high) < 0.005) return `$${formattedLow}${suffix}`;
  return `$${formattedLow}–$${formattedHigh}${suffix}`;
};

/** Scenario Analysis: `$XM` point or `$XM - $YM` band (values already in $M). */
function formatScenarioAnalysisRoiRangeM(lowM: number, highM: number): string {
  if (!Number.isFinite(lowM) || !Number.isFinite(highM) || (lowM <= 0 && highM <= 0)) return 'N/A';
  if (Math.abs(lowM - highM) < 0.005) return `$${formatRoiValue(lowM)}M`;
  return `$${formatRoiValue(lowM)}M - $${formatRoiValue(highM)}M`;
}

const SHORTLIST_BUCKET_LABELS: Record<InitiativeShortlistBucket, string> = {
  launch: 'Launch Now',
  pilot: 'Pilot',
  monitor: 'Monitor',
  defer: 'Defer',
};

/** Map list API `value_band` (including Significant / Low) to UI confidence. */
const confidenceFromValueBand = (band: string): ConfidenceLabel => {
  const b = band.trim().toLowerCase();
  if (b === 'high' || b === 'significant') return 'High';
  if (b === 'moderate') return 'Medium';
  return 'Low';
};

/** Surfaced-reason label for UCE list API mapping. */
const UCE_CATEGORY_FIT_CONTEXT_LABEL = 'Category fit';

const toMillions = (value: number): number => {
  if (value < 100) return value;
  return value / 1_000_000;
};

// ============================================================
// TYPES (Page-specific extensions)
// ============================================================

interface SurfacedReason {
  contextLabel: string;
  contextValue: string;
  relevance: string;
}

type ConfidenceLabel = 'High' | 'Medium' | 'Low';

interface OpportunityAssumption {
  id: string;
  type?: string;
  category?: string;
  label: string;
  value: string;
  confidence?: ConfidenceLabel | string;
  source?: string;
}

interface Opportunity {
  id: string;
  rank: number;
  title: string;
  category: string;
  description: string;
  implementationEffort: 'Low' | 'Medium' | 'High';
  timeToValue: string;
  roiEstimate: { low: number; high: number; unit?: string };
  surfacedReasons: SurfacedReason[];
  assumptions: OpportunityAssumption[];
  scenarioAssumptions: {
    base: OpportunityAssumption[];
    conservative: OpportunityAssumption[];
    aggressive: OpportunityAssumption[];
  };
  scenarios: {
    conservative: {
      roiLow?: number;
      roiHigh?: number;
      paybackMonths?: number;
      confidence?: number | ConfidenceLabel;
      annualBenefit?: number;
    };
    base: {
      roiLow?: number;
      roiHigh?: number;
      paybackMonths?: number;
      confidence?: number | ConfidenceLabel;
      annualBenefit?: number;
    };
    aggressive: {
      roiLow?: number;
      roiHigh?: number;
      paybackMonths?: number;
      confidence?: number | ConfidenceLabel;
      annualBenefit?: number;
    };
  };
  primaryValueDriver: string;
  primaryRisk: string;
  keyRisks?: string[];
  keyBenefits?: string[];
  validationMetrics?: string[];
  confidence: ConfidenceLabel;
  valueBand?: string;
  totalScore?: number;
}

type EffortFilter = 'all' | 'Low' | 'Medium' | 'High';
type TimeFilter = 'all' | 'under6' | '6to12' | 'over12';
type ValueFilter = 'all' | 'under2' | '2to5' | 'over5';

const EFFORT_TO_TAG: Record<Opportunity['implementationEffort'], EffortTag> = {
  Low: 'Quick Win',
  Medium: 'Moderate',
  High: 'High',
};

/** Same EOI→effort bands as Scoring Rationale / backend deriveEffortTier (DMD listing). */
function effortTierFromImplementationScore(score: number | null | undefined): Opportunity['implementationEffort'] {
  if (score == null || !Number.isFinite(score)) return 'High';
  if (score >= 60) return 'Low';
  if (score >= 40) return 'Medium';
  return 'High';
}

function effortTagFromUce(uce: UCEOpportunity): EffortTag {
  const tier =
    effortTierFromImplementationScore(uce.implementation_score ?? uce.ease_of_implementation_score) ||
    uce.effort_tier;
  return EFFORT_TO_TAG[tier] ?? 'High';
}

function toExplorerBrief(opp: Opportunity): ExplorerRowBrief {
  return {
    id: opp.id,
    rank: opp.rank,
    title: opp.title,
    category: opp.category,
    verdict: verdictFromScore(opp.totalScore, opp.valueBand ?? opp.confidence),
    roiShort: formatRoiRange(opp.roiEstimate.low, opp.roiEstimate.high, opp.roiEstimate.unit),
    ttv: opp.timeToValue,
    effortTag: EFFORT_TO_TAG[opp.implementationEffort] ?? 'High',
  };
}

function uceToExplorerBrief(uce: UCEOpportunity, idx = 0): ExplorerRowBrief {
  const baseM = dollarsOrMToMillions(uce.annual_benefit_base ?? uce.financial_composite);
  const consM = dollarsOrMToMillions(uce.annual_benefit_conservative) || baseM;
  const aggrM = dollarsOrMToMillions(uce.annual_benefit_aggressive) || baseM;
  const roiLow = Math.min(consM || baseM, aggrM || baseM);
  const roiHigh = Math.max(consM || baseM, aggrM || baseM);
  return {
    id: String(uce.use_case_id),
    rank: uce.rank ?? idx + 1,
    title: uce.name,
    category: uce.categoryFit?.trim() || uce.category_name || 'General',
    verdict: verdictFromScore(uce.total_score, String(uce.value_band ?? '')),
    roiShort: formatRoiRange(roiLow, roiHigh, 'M'),
    ttv: uce.timeline,
    effortTag: effortTagFromUce(uce),
  };
}

// ============================================================
// MAIN PAGE COMPONENT
// ============================================================

const ITEMS_PER_PAGE = 15;

/** DMD-1198: persist Evaluate tab in the URL so browser Back restores Use Case Explorer. */
const EVALUATE_TAB_PARAM = 'tab';

export default function EAOpportunitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const compare = useCompareSelection();

  interface RestoreState {
    scrollY: number;
    useCaseId: string;
  }

  const { plan, isPro, isFreemium, upgrade } = useDemoPlan();
  useAuth(); // session intentionally ignored while client is pinned
  const clientId = EA_CLIENT_ID;
  const cachedCatalog = peekOpportunities(String(clientId), UCE_CATALOG_FILTERS);

  // DMD-1064: org checkbox filters use_cases.tenant_id (session-persisted).
  // Scores/selection stay on journey EA_CLIENT_ID; list filtering uses tenantId query param.
  const [orgFilter, setOrgFilter] = useState<string>(() => readOrgFilterSession());
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  const orgFilterActive = orgFilter !== ORG_FILTER_ALL;
  const journeyOrg =
    orgOptions.find((o) => o.id === String(clientId)) ??
    orgOptions[0] ??
    { id: String(clientId), name: 'My organization' };
  /** Checked → filter to this org's tenant_id; unchecked → full list (incl. standard library). */
  const listTenantId = orgFilterActive ? orgFilter : undefined;

  // ── Portfolio Intelligence API state ────────────────────────────────────
  const [piReport, setPiReport] = useState<PIReport | null>(null);
  const [piLoading, setPiLoading] = useState(false);
  const [piError, setPiError] = useState<string | null>(null);

  const {
    finalistIds,
    finalistOrder,
    setFinalistIds,
    addFinalist,
    removeFinalist,
    toggleFinalist,
    initiativeShortlistCount,
    initiativeShortlistBuckets,
  } = useEA();

  const [chatOpen, setChatOpen] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>('rank');
  // ── Use Case Explorer API state ───────────────────────────────────────────
  const [explorerData, setExplorerData] = useState<UCEOpportunity[]>([]);
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [explorerError, setExplorerError] = useState<string | null>(null);
  const [explorerTotal, setExplorerTotal] = useState(0);
  /** DMD-1063: when portfolio metrics were last computed (from Explorer API). */
  const [metricsLastComputedAt, setMetricsLastComputedAt] = useState<string | null>(
    () => cachedCatalog?.metrics?.last_computed_at ?? null,
  );
  const [selectionSyncError, setSelectionSyncError] = useState<string | null>(null);
  /** Full UCE catalog so Portfolio Intelligence top-10 matches Explorer est. values */
  const [uceCatalog, setUceCatalog] = useState<UCEOpportunity[]>(() => cachedCatalog?.data ?? []);
  const [uceCatalogTotal, setUceCatalogTotal] = useState(
    () => cachedCatalog?.pagination?.total || cachedCatalog?.data?.length || 0,
  );
  const [catalogStatus, setCatalogStatus] = useState<'pending' | 'ready'>(() =>
    cachedCatalog ? 'ready' : 'pending',
  );
  // Snapshots of selected initiatives — survives pagination / tab switches so
  // Executive Summary "Top Selected Initiatives" matches the user's portfolio.
  const [finalistSnapshots, setFinalistSnapshots] = useState<Record<string, ReportOpportunity>>(
    () => loadFinalistSnapshots(),
  );
  /** IDs already hydrated from UCE (explorer page or fetchOpportunity) this session. */
  const uceHydratedIdsRef = useRef<Set<string>>(new Set());

  // DMD-1198: URL `?tab=` is the source of truth so browser Back restores the right Evaluate tab.
  const activeTab = resolveEvaluateTab(searchParams.get(EVALUATE_TAB_PARAM));

  const writeEvaluateTab = (tab: EvaluateTab) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === 'report') nextParams.delete(EVALUATE_TAB_PARAM);
    else nextParams.set(EVALUATE_TAB_PARAM, tab);
    const search = nextParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : '',
        hash: location.hash,
      },
      { replace: true, state: location.state },
    );
  };

  const [showFilters, _setShowFilters] = useState(false); // unused — filters now always visible
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [effortFilter, setEffortFilter] = useState<EffortFilter>('all');
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');
  const [valueFilter, setValueFilter] = useState<ValueFilter>('all');
  const [categoryOptions, setCategoryOptions] = useState<string[]>(() => {
    if (!cachedCatalog?.data?.length) return [];
    const cats = new Set(
      cachedCatalog.data
        .map((o) => o.categoryFit?.trim() || o.category_name)
        .filter(Boolean) as string[],
    );
    return Array.from(cats).sort();
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [showExecuteConfirm, setShowExecuteConfirm] = useState(false);

  const initiativeScore = initiativesScoreFromFinalistCount(finalistIds.size);
  const { personalizationData } = useEAPersonalizationProgress(String(clientId), initiativeScore);

  // DMD-1064: load organizations for the Explore org filter
  useEffect(() => {
    let cancelled = false;
    const apiBase = (
      import.meta.env.VITE_API_URL ||
      import.meta.env.VITE_BACKEND_URL ||
      'http://localhost:8080'
    ).replace(/\/+$/, '');

    fetch(`${apiBase}/api/v2/clients`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`clients ${res.status}`);
        return res.json();
      })
      .then((payload) => {
        if (cancelled) return;
        const list = normalizeClientList(payload);
        // Ensure the journey org is always selectable even if the API omits it.
        if (!list.some((o) => o.id === String(clientId))) {
          list.unshift({ id: String(clientId), name: 'My organization' });
        }
        setOrgOptions(list);
        // Drop stale session ids that are no longer in the directory.
        if (orgFilter !== ORG_FILTER_ALL && !list.some((o) => o.id === orgFilter)) {
          setOrgFilter(ORG_FILTER_ALL);
          writeOrgFilterSession(ORG_FILTER_ALL);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setOrgOptions([{ id: String(clientId), name: 'My organization' }]);
      });

    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    writeOrgFilterSession(orgFilter);
  }, [orgFilter]);

  // Heavy PI report only when the Report tab is visible.
  useEffect(() => {
    if (!clientId || activeTab !== 'report') return;
    if (piReport) return;
    let cancelled = false;
    setPiLoading(true);
    setPiError(null);
    fetchPortfolioIntelligenceReport(clientId)
      .then((report) => {
        if (!cancelled) setPiReport(report);
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[PortfolioIntelligence] API fetch failed:', err.message);
          setPiError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setPiLoading(false);
      });
    return () => { cancelled = true; };
  }, [clientId, activeTab, piReport]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    fetchOpportunities(String(clientId), UCE_CATALOG_FILTERS)
      .then((res) => {
        if (cancelled) return;
        setUceCatalog(res.data ?? []);
        setUceCatalogTotal(res.pagination?.total || res.data?.length || 0);
        if (res.metrics?.last_computed_at) {
          setMetricsLastComputedAt(res.metrics.last_computed_at);
        }
        const cats = new Set(
          res.data
            .map((o) => o.categoryFit?.trim() || o.category_name)
            .filter(Boolean) as string[],
        );
        setCategoryOptions(Array.from(cats).sort());
        setCatalogStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setUceCatalog([]);
          setUceCatalogTotal(0);
          setCategoryOptions([]);
          setCatalogStatus('ready');
        }
      });
    return () => { cancelled = true; };
  }, [clientId]);

  // Apply one-shot restore scroll / returnToTab, then clear those keys without
  // wiping React Router history state (raw replaceState broke Back — DMD-1198).
  useEffect(() => {
    const state = location.state as
      | {
          restore?: RestoreState;
          restoreScrollY?: number;
          returnToTab?: string;
          [key: string]: unknown;
        }
      | null;
    if (!state) return;

    const restore = state.restore;
    const restoreScrollY = state.restoreScrollY;
    const returnToTab = state.returnToTab;
    const needsScroll = restore?.scrollY !== undefined || restoreScrollY !== undefined;
    const needsTabSync = returnToTab != null;
    if (!needsScroll && !needsTabSync) return;

    if (restore?.scrollY !== undefined) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: restore.scrollY, behavior: 'auto' });
      });
    } else if (restoreScrollY !== undefined) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: restoreScrollY, behavior: 'auto' });
      });
    }

    const nextParams = new URLSearchParams(location.search);
    if (needsTabSync) {
      const desired = resolveEvaluateTab(returnToTab);
      if (desired === 'report') nextParams.delete(EVALUATE_TAB_PARAM);
      else nextParams.set(EVALUATE_TAB_PARAM, desired);
    }
    const search = nextParams.toString();
    const searchStr = search ? `?${search}` : '';

    const prevState = { ...state };
    delete prevState.restore;
    delete prevState.restoreScrollY;
    delete prevState.returnToTab;

    navigate(
      {
        pathname: location.pathname,
        search: searchStr,
        hash: location.hash,
      },
      {
        replace: true,
        state: Object.keys(prevState).length > 0 ? prevState : null,
      },
    );
  }, [location.state, location.pathname, location.search, location.hash, navigate]);

  const uniqueCategories = useMemo(() => ['all', ...categoryOptions], [categoryOptions]);

  const finalistCount = finalistIds.size;
  const initiativeCount = finalistCount;
  const hasPortfolioInitiative = initiativeCount >= 1;
  const hasShortlistSelection = initiativeShortlistCount >= 1;

  const personalizationProgressForGate = useMemo(
    () => ({
      profile: personalizationData.profile,
      surveys: personalizationData.surveys,
      documents: personalizationData.documents,
    }),
    [personalizationData],
  );

  const hasPersonalizationComplete = hasCorePersonalizationComplete(personalizationProgressForGate);
  const personalizationBlockers = getPersonalizationBlockers(personalizationProgressForGate);
  // Show as soon as the portfolio has ≥1 initiative. Shortlist posture and personalization
  // strength stay as soft hints — they no longer hide Continue to Execute.
  const showContinueToExecute = isPro && hasPortfolioInitiative;

  // ── Use Case Explorer: fetch from API when explore tab is active ─────────
  useEffect(() => {
    if (activeTab !== 'explore' || !clientId) return;
    let cancelled = false;

    const usingUnfilteredCatalog = !orgFilterActive;
    const defaultsOnly =
      currentPage === 1
      && sortOption === 'rank'
      && categoryFilter === 'all'
      && effortFilter === 'all'
      && timeFilter === 'all'
      && valueFilter === 'all'
      && usingUnfilteredCatalog;

    // Default Explore page 1 is already covered by the 200-row catalog — slice it.
    if (defaultsOnly && uceCatalog.length > 0) {
      setExplorerData(uceCatalog.slice(0, ITEMS_PER_PAGE));
      setExplorerTotal(uceCatalogTotal || uceCatalog.length);
      setExplorerLoading(false);
      setExplorerError(null);
      return;
    }
    // Wait for the catalog instead of also requesting pageSize=15.
    if (defaultsOnly && catalogStatus === 'pending') {
      setExplorerLoading(true);
      setExplorerError(null);
      return;
    }

    const params: Record<string, string | number> = {
      page: currentPage,
      pageSize: ITEMS_PER_PAGE,
    };

    // Map local sort → API sortBy param
    if (sortOption === 'roi_high') { params.sortBy = 'financial'; params.sortOrder = 'desc'; }
    else if (sortOption === 'effort') { params.sortBy = 'implementation'; params.sortOrder = 'desc'; } // easiest first
    else if (sortOption === 'confidence') { params.sortBy = 'value'; params.sortOrder = 'desc'; }
    else if (sortOption === 'time_to_value' || sortOption === 'payback') {
      params.sortBy = sortOption === 'payback' ? 'payback' : 'timeline';
      params.sortOrder = 'asc';
    }
    else { params.sortBy = 'rank'; params.sortOrder = 'asc'; }

    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (effortFilter !== 'all') params.effort = effortFilter;
    if (timeFilter === 'under6') params.timeline = '0-3 months,3-6 months';
    else if (timeFilter === '6to12') params.timeline = '6-9 months,9-12 months';
    else if (timeFilter === 'over12') params.timeline = 'over 12 months';
    // Dollar buckets on annual_benefit_base (financial_composite) — not value_band score labels
    if (valueFilter === 'under2') params.valueMax = 2_000_000;
    else if (valueFilter === '2to5') { params.valueMin = 2_000_000; params.valueMax = 5_000_000; }
    else if (valueFilter === 'over5') params.valueMin = 5_000_000;
    // DMD-1064: filter use_cases by tenant_id (org checkbox)
    if (listTenantId) params.tenantId = listTenantId;

    setExplorerLoading(true);
    setExplorerError(null);
    fetchOpportunities(String(clientId), params as Parameters<typeof fetchOpportunities>[1])
      .then((res) => {
        if (!cancelled) {
          setExplorerData(res.data);
          setExplorerTotal(res.pagination.total);
          if (res.metrics?.last_computed_at) {
            setMetricsLastComputedAt(res.metrics.last_computed_at);
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[UseCaseExplorer] API unavailable:', err.message);
          setExplorerError(err.message);
        }
      })
      .finally(() => { if (!cancelled) setExplorerLoading(false); });

    return () => { cancelled = true; };
  }, [activeTab, clientId, listTenantId, currentPage, sortOption, categoryFilter, effortFilter, timeFilter, valueFilter, uceCatalog, uceCatalogTotal, catalogStatus]);

  // ── Sync finalistIds with portfolio selection API ────────────────────────
  // The selection API is the source of truth for "Added" / portfolio count.
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;

    getSelection(String(clientId))
      .then((res) => {
        if (cancelled) return;

        const apiIds = (res.selected_ids || []).map(String);
        setFinalistIds(new Set(apiIds));

        if (!cancelled) setSelectionSyncError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        // Keep whatever EAContext already hydrated from DB / localStorage cache
        const message = err instanceof Error ? err.message : 'Failed to load selection';
        console.warn('[UCE Selection] Failed to load selection — using evaluate-state cache:', message);
        setSelectionSyncError(message);
      });

    return () => { cancelled = true; };
  }, [clientId, setFinalistIds]);

  // Fallback: when selection API is unavailable, seed from list `is_selected` flags
  useEffect(() => {
    if (!selectionSyncError || explorerData.length === 0 || finalistIds.size > 0) return;
    const fromList = explorerData
      .filter((o) => o.is_selected)
      .map((o) => String(o.use_case_id));
    if (fromList.length > 0) {
      setFinalistIds(new Set(fromList));
    }
  }, [selectionSyncError, explorerData, finalistIds.size, setFinalistIds]);

  // ── Map API opportunities to the UI Opportunity shape ────────────────────
  const apiExplorerOpps = useMemo((): Opportunity[] => {
    if (!explorerData.length) return [];
    return explorerData.map((uce, idx) => {
      const baseM = dollarsOrMToMillions(uce.annual_benefit_base ?? uce.financial_composite);
      const consM = dollarsOrMToMillions(uce.annual_benefit_conservative) || baseM;
      const aggrM = dollarsOrMToMillions(uce.annual_benefit_aggressive) || baseM;
      // Header est. value = cons–aggr band (matches SEP Selected Initiatives / Playbook).
      const roiLow = Math.min(consM || baseM, aggrM || baseM);
      const roiHigh = Math.max(consM || baseM, aggrM || baseM);
      const pb = uce.payback_months;
      const basePayback =
        pb != null && Number.isFinite(pb) && pb > 0 ? Math.round(pb) : 12;
      const consPb = uce.payback_months_conservative;
      const aggrPb = uce.payback_months_aggressive;
      const conservativePayback =
        consPb != null && Number.isFinite(consPb) && consPb > 0
          ? Math.round(consPb)
          : basePayback;
      const aggressivePayback =
        aggrPb != null && Number.isFinite(aggrPb) && aggrPb > 0
          ? Math.round(aggrPb)
          : basePayback;
      const confidence = confidenceFromValueBand(String(uce.value_band));
      const implScore = uce.ease_of_implementation_score ?? uce.implementation_score;
      const categoryFitLabel = uce.categoryFit?.trim() || uce.category_name;
      return {
        id: String(uce.use_case_id),
        rank: uce.rank ?? (idx + 1),
        title: uce.name,
        category: categoryFitLabel,
        valueBand: String(uce.value_band ?? '').trim() || undefined,
        totalScore: uce.total_score,
        description: uce.description || `Analytics initiative in the ${categoryFitLabel} domain.`,
        implementationEffort: effortTierFromImplementationScore(
          uce.implementation_score ?? uce.ease_of_implementation_score,
        ),
        timeToValue: uce.timeline,
        roiEstimate: { low: roiLow, high: roiHigh, unit: 'M' },
        surfacedReasons: [
          {
            contextLabel: UCE_CATEGORY_FIT_CONTEXT_LABEL,
            contextValue: categoryFitLabel,
            relevance: `${categoryFitLabel} · value band ${uce.value_band}`,
          },
          {
            contextLabel: 'Portfolio Rank',
            contextValue: String(uce.rank ?? idx + 1),
            relevance: `Composite score ${formatNumber2(uce.total_score)}/100 with value ${formatNumber2(uce.value_score)}/100 and implementation ${formatNumber2(implScore)}/100`,
          },
          {
            contextLabel: 'Time To Value',
            contextValue: uce.timeline,
            relevance: `Expected effort tier ${uce.effort_tier} and delivery window ${uce.timeline}`,
          },
        ],
        assumptions: [
          {
            type: 'IMPACT',
            label: 'Value score assumption',
            value: `${formatNumber2(uce.value_score)}/100`,
            confidence,
            source: 'Use Case Explorer list API',
            id: `${uce.use_case_id}-impact`,
          },
          {
            type: 'FINANCIAL',
            label: 'ROI range (annual benefit band)',
            value:
              uce.roi_range ??
              (roiLow > 0
                ? formatScenarioAnalysisRoiRangeM(roiLow, roiHigh)
                : 'Scoring pending'),
            confidence: 'Medium',
            source: 'Use Case Explorer list API',
            id: `${uce.use_case_id}-financial`,
          },
          {
            type: 'TIMELINE',
            label: 'Implementation timeline assumption',
            value: uce.timeline,
            confidence: 'Medium',
            source: 'Use Case Explorer list API',
            id: `${uce.use_case_id}-timeline`,
          },
        ],
        scenarioAssumptions: { base: [], conservative: [], aggressive: [] },
        scenarios: {
          // Scenario tabs = ROI-model annual benefit point (matches SEP waterfall).
          base: {
            paybackMonths: basePayback,
            confidence: 0.6,
            annualBenefit: baseM,
            roiLow: baseM,
            roiHigh: baseM,
          },
          conservative: {
            paybackMonths: conservativePayback,
            confidence: 0.7,
            annualBenefit: consM,
            roiLow: consM,
            roiHigh: consM,
          },
          aggressive: {
            paybackMonths: aggressivePayback,
            confidence: 0.4,
            annualBenefit: aggrM,
            roiLow: aggrM,
            roiHigh: aggrM,
          },
        },
        primaryValueDriver: `Value score ${formatNumber2(uce.value_score)}/100 (category fit: ${categoryFitLabel})`,
        primaryRisk: uce.primary_risk
          ? `${uce.primary_risk} risk · implementation ${formatNumber2(implScore)}/100 (${uce.effort_tier} effort)`
          : `Implementation score ${formatNumber2(implScore)}/100 with ${uce.effort_tier} effort`,
        keyRisks: [],
        keyBenefits: [],
        validationMetrics: [],
        confidence,
      } as Opportunity;
    });
  }, [explorerData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, effortFilter, timeFilter, valueFilter, sortOption]);

  useEffect(() => {
    if (isFreemium && finalistIds.size > 0) {
      Array.from(finalistIds).forEach(id => toggleFinalist(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFreemium]);

  const useApiExplorer = apiExplorerOpps.length > 0;
  const displayOpps = useApiExplorer ? apiExplorerOpps : [];
  const displayTotal = useApiExplorer ? explorerTotal : 0;
  const displayTotalPages = useApiExplorer ? Math.ceil(explorerTotal / ITEMS_PER_PAGE) : 0;
  const lastComputedLabel = useMemo(
    () => formatPortfolioLastComputedLabel(metricsLastComputedAt),
    [metricsLastComputedAt],
  );

  const activeFilterCount = [
    orgFilterActive,
    categoryFilter !== 'all',
    effortFilter !== 'all',
    timeFilter !== 'all',
    valueFilter !== 'all',
  ].filter(Boolean).length;

  const selectedFromExplorer = useMemo(
    () => apiExplorerOpps.filter(o => finalistIds.has(o.id)),
    [apiExplorerOpps, finalistIds],
  );
  const selectedRoiLow = selectedFromExplorer.reduce((sum, o) => sum + toMillions(o.roiEstimate.low), 0);
  const selectedRoiHigh = selectedFromExplorer.reduce((sum, o) => sum + toMillions(o.roiEstimate.high), 0);
  const selectedPortfolioRangeLabel = finalistCount > 0
    ? (selectedFromExplorer.length > 0
      ? `$${selectedRoiLow.toFixed(2)}M – $${selectedRoiHigh.toFixed(2)}M`
      : '—')
    : '—';

  // ── Portfolio Intelligence API-derived report props ────────────────────
  const apiOpportunities: ReportOpportunity[] | null = useMemo(() => {
    if (!piReport) return null;
    return mapReportToOpportunities(piReport);
  }, [piReport]);

  const reportClientName = piReport?.sections?.strategic_context?.organization_name
    || 'Organization';
  const reportTotalCount = piReport?.sections?.executive_summary?.use_case_count ?? 0;
  const reportRankedOpps = apiOpportunities ?? [];

  // Top 10 = Use Case Explorer rank order + est. values (DVA-UI-073 / Financial Impact parity).
  // Fall back to PI canvas ranking only when the UCE catalog is unavailable.
  const reportTop10 = useMemo(() => {
    if (uceCatalog.length > 0) {
      return [...uceCatalog]
        .sort((a, b) => (a.rank ?? Number.POSITIVE_INFINITY) - (b.rank ?? Number.POSITIVE_INFINITY))
        .slice(0, 10)
        .map((uce, idx) => uceToReportSnapshot(uce, idx));
    }
    return reportRankedOpps.slice(0, 10);
  }, [uceCatalog, reportRankedOpps]);

  // Sum of the same top-10 est. bands shown in Use Case Explorer — never full-portfolio annualValue*.
  const reportPortfolioRangeLabel = useMemo(() => {
    if (reportTop10.length === 0) return '—';
    const low = reportTop10.reduce((s, o) => s + (o.roiEstimate.low || 0), 0);
    const high = reportTop10.reduce((s, o) => s + (o.roiEstimate.high || 0), 0);
    if (low <= 0 && high <= 0) return '—';
    return `$${low.toFixed(2)}M – $${high.toFixed(2)}M`;
  }, [reportTop10]);

  // Waterfall benefits from the same top-10 rollup (not inflated API annualValue / full-portfolio waterfall).
  const reportWaterfall = useMemo(() => {
    if (reportTop10.length === 0) return piReport?.sections?.financial_impact?.waterfall;

    const sumLow = reportTop10.reduce((s, o) => s + (o.roiEstimate.low || 0), 0);
    const sumHigh = reportTop10.reduce((s, o) => s + (o.roiEstimate.high || 0), 0);
    const sumBase = reportTop10.reduce((s, o) => {
      const base = o.roiEstimate.base;
      if (base != null && base > 0) return s + base;
      return s + ((o.roiEstimate.low || 0) + (o.roiEstimate.high || 0)) / 2;
    }, 0);

    const apiWf = piReport?.sections?.financial_impact?.waterfall;
    const apiBaseBenefits = apiWf?.base?.benefits ?? 0;
    const apiCosts = apiWf?.base?.costs ?? 0;
    // Scale API costs down when they were computed against the inflated full-portfolio benefits
    const costScale =
      apiBaseBenefits > 0 && apiBaseBenefits > sumBase * 1.5
        ? sumBase / apiBaseBenefits
        : 1;
    const costs = Math.max(0, apiCosts * costScale);

    return {
      conservative: { benefits: sumLow, costs, net: sumLow - costs },
      base: { benefits: sumBase, costs, net: sumBase - costs },
      aggressive: { benefits: sumHigh, costs, net: sumHigh - costs },
    };
  }, [reportTop10, piReport]);

  const apiTop3Opps = useMemo(() => {
    const top3 = piReport?.sections?.executive_summary?.top_recommended;
    if (!top3 || !apiOpportunities) return undefined;
    return top3
      .map(r => apiOpportunities.find(o => o.id === String(r.use_case_id)))
      .filter((o): o is ReportOpportunity => Boolean(o));
  }, [piReport, apiOpportunities]);

  // User's portfolio selections — ordered by finalistOrder, then remaining IDs.
  // Used for Executive Summary "Top Selected Initiatives" (must NOT use API top_recommended).
  //
  // Metrics MUST match Use Case Explorer (effort_tier, timeline, Est. value).
  // Do NOT prefer PI decision-canvas rows — those map execution_posture → effort and
  // payback_months → time-to-value, which diverged from UCE (DVA-UI-062).
  const reportSelectedOpps = useMemo(() => {
    if (finalistIds.size === 0) return [];

    const finalistIdSet = new Set(Array.from(finalistIds, String));
    const byId = new Map<string, ReportOpportunity>();

    // 1) Persisted UCE-sourced snapshots (stable across tabs/pages)
    for (const [id, snap] of Object.entries(finalistSnapshots)) {
      const sid = String(id);
      if (finalistIdSet.has(sid) && snap?.title) byId.set(sid, { ...snap, id: sid });
    }
    // 2) Live Use Case Explorer rows win (same source as the explorer list UI)
    for (const opp of apiExplorerOpps) {
      const id = String(opp.id);
      if (finalistIdSet.has(id)) {
        byId.set(id, toReportOpportunitySnapshot(opp));
      }
    }
    // 3) Full UCE catalog fills selections that are off the current explorer page
    for (const uce of uceCatalog) {
      const id = String(uce.use_case_id);
      if (finalistIdSet.has(id) && !byId.has(id)) {
        byId.set(id, uceToReportSnapshot(uce));
      }
    }
    // 4) PI canvas only fills gaps still missing after UCE sources
    for (const opp of apiOpportunities ?? []) {
      const id = String(opp.id);
      if (finalistIdSet.has(id) && !byId.has(id)) {
        byId.set(id, { ...opp, id });
      }
    }

    const ordered: ReportOpportunity[] = [];
    const seen = new Set<string>();

    for (const id of finalistOrder) {
      const sid = String(id);
      const opp = byId.get(sid);
      if (opp && finalistIdSet.has(sid)) {
        ordered.push(opp);
        seen.add(sid);
      }
    }
    for (const id of finalistIdSet) {
      const sid = String(id);
      if (seen.has(sid)) continue;
      const opp = byId.get(sid);
      if (opp) {
        ordered.push(opp);
        seen.add(sid);
      }
    }
    return ordered;
  }, [apiOpportunities, apiExplorerOpps, uceCatalog, finalistIds, finalistOrder, finalistSnapshots]);

  const carryForwardCount = reportSelectedOpps.length;
  const carryForwardValueLabel = useMemo(() => {
    if (reportSelectedOpps.length === 0) return '—';
    const low = reportSelectedOpps.reduce((sum, opp) => sum + (opp.roiEstimate.low || 0), 0);
    const high = reportSelectedOpps.reduce((sum, opp) => sum + (opp.roiEstimate.high || 0), 0);
    if (low <= 0 && high <= 0) return '—';
    return `$${low.toFixed(2)}M – $${high.toFixed(2)}M`;
  }, [reportSelectedOpps]);

  // Drop selection IDs that are not in the ranked catalog (stale DB/local leftovers).
  useEffect(() => {
    if (!clientId || uceCatalog.length === 0 || finalistIds.size === 0) return;

    const catalogIds = new Set(uceCatalog.map((uce) => String(uce.use_case_id)));
    const validIds = Array.from(finalistIds, (id) => String(id)).filter((id) => catalogIds.has(id));
    const ghostIds = Array.from(finalistIds, (id) => String(id)).filter((id) => !catalogIds.has(id));
    if (ghostIds.length === 0) return;

    setFinalistIds(new Set(validIds));
    ghostIds.forEach((id) => {
      const numericId = Number.parseInt(id, 10);
      if (!Number.isFinite(numericId)) return;
      void deselectUseCase(String(clientId), numericId).catch(() => undefined);
    });
  }, [clientId, uceCatalog, finalistIds, setFinalistIds]);

  // Keep snapshots in sync from Use Case Explorer only — never overwrite with PI posture metrics.
  useEffect(() => {
    setFinalistSnapshots((prev) => {
      if (finalistIds.size === 0) {
        if (Object.keys(prev).length === 0) return prev;
        saveFinalistSnapshots({});
        return {};
      }

      const next = { ...prev };
      let changed = false;

      for (const id of Object.keys(next)) {
        if (!finalistIds.has(id)) {
          delete next[id];
          changed = true;
        }
      }

      const upsert = (snap: ReportOpportunity) => {
        const existing = next[snap.id];
        if (
          !existing ||
          existing.title !== snap.title ||
          existing.roiEstimate.low !== snap.roiEstimate.low ||
          existing.roiEstimate.high !== snap.roiEstimate.high ||
          existing.implementationEffort !== snap.implementationEffort ||
          existing.timeToValue !== snap.timeToValue ||
          existing.valueBand !== snap.valueBand
        ) {
          next[snap.id] = snap;
          changed = true;
        }
      };

      for (const opp of apiExplorerOpps) {
        if (finalistIds.has(opp.id)) upsert(toReportOpportunitySnapshot(opp));
      }

      if (!changed) return prev;
      saveFinalistSnapshots(next);
      return next;
    });
  }, [apiExplorerOpps, finalistIds]);

  // Fetch UCE metrics for selected IDs not yet hydrated from the explorer list.
  // Prefer Use Case Explorer so Executive Summary matches Est. value / timeline / effort.
  useEffect(() => {
    if (!clientId || finalistIds.size === 0) return;

    for (const id of [...uceHydratedIdsRef.current]) {
      if (!finalistIds.has(id)) uceHydratedIdsRef.current.delete(id);
    }

    const onExplorerPage = new Set(apiExplorerOpps.map((o) => o.id));
    for (const id of onExplorerPage) {
      if (finalistIds.has(id)) uceHydratedIdsRef.current.add(id);
    }

    // Prefer the already-loaded 200-row catalog before per-ID opportunity fetches.
    const fromCatalog: ReportOpportunity[] = [];
    for (const id of finalistIds) {
      if (uceHydratedIdsRef.current.has(id)) continue;
      const uce = uceCatalog.find((row) => String(row.use_case_id) === id);
      if (!uce) continue;
      uceHydratedIdsRef.current.add(id);
      fromCatalog.push(uceToReportSnapshot(uce, fromCatalog.length));
    }
    if (fromCatalog.length > 0) {
      setFinalistSnapshots((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const snap of fromCatalog) {
          if (!finalistIds.has(snap.id)) continue;
          const existing = next[snap.id];
          if (
            !existing ||
            existing.title !== snap.title ||
            existing.roiEstimate.low !== snap.roiEstimate.low ||
            existing.roiEstimate.high !== snap.roiEstimate.high ||
            existing.implementationEffort !== snap.implementationEffort ||
            existing.timeToValue !== snap.timeToValue
          ) {
            next[snap.id] = snap;
            changed = true;
          }
        }
        if (!changed) return prev;
        saveFinalistSnapshots(next);
        return next;
      });
    }

    const toFetch = Array.from(finalistIds).filter((id) => !uceHydratedIdsRef.current.has(id));
    if (toFetch.length === 0) return;

    let cancelled = false;
    (async () => {
      const fetched: ReportOpportunity[] = [];
      await Promise.all(
        toFetch.map(async (id, idx) => {
          try {
            const uce = await fetchOpportunity(String(clientId), id);
            fetched.push(uceToReportSnapshot(uce, idx));
          } catch (err) {
            console.warn('[EA] Failed to resolve selected initiative', id, err);
          }
        }),
      );
      if (cancelled || fetched.length === 0) return;
      setFinalistSnapshots((prev) => {
        const next = { ...prev };
        let changed = false;
        for (const snap of fetched) {
          if (!finalistIds.has(snap.id)) continue;
          uceHydratedIdsRef.current.add(snap.id);
          const existing = next[snap.id];
          if (
            !existing ||
            existing.title !== snap.title ||
            existing.roiEstimate.low !== snap.roiEstimate.low ||
            existing.roiEstimate.high !== snap.roiEstimate.high ||
            existing.implementationEffort !== snap.implementationEffort ||
            existing.timeToValue !== snap.timeToValue
          ) {
            next[snap.id] = snap;
            changed = true;
          }
        }
        if (!changed) return prev;
        saveFinalistSnapshots(next);
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId, finalistIds, apiExplorerOpps, uceCatalog]);
  const reportFinalistCount = reportSelectedOpps.length || finalistCount;
  const reportShortlistedLabel = reportFinalistCount > 0 && reportSelectedOpps.length > 0
    ? (() => {
      const low = reportSelectedOpps.reduce((s, o) => s + o.roiEstimate.low, 0);
      const high = reportSelectedOpps.reduce((s, o) => s + o.roiEstimate.high, 0);
      return `$${low.toFixed(2)}M – $${high.toFixed(2)}M`;
    })()
    : selectedPortfolioRangeLabel;

  // ── Build API roadmap buckets for OIRSectionExecutionRoadmap ────────────
  // Maps piReport.sections.roadmap phases → the Opportunity shape the component expects.
  const RAW_TO_M = (v: number) => (Math.abs(v) >= 1_000 ? v / 1_000_000 : v);
  const apiRoadmapBuckets = useMemo((): ApiRoadmapBuckets | undefined => {
    const roadmap = piReport?.sections?.roadmap;
    if (!roadmap) return undefined;

    const phaseToOpps = (
      initiatives: typeof roadmap.phases.launch_now.initiatives,
      effort: 'Low' | 'Medium' | 'High',
    ) =>
      initiatives.map((init, idx) => ({
        id: String(init.use_case_id),
        rank: idx + 1,
        title: init.name,
        implementationEffort: effort,
        roiEstimate: {
          low: RAW_TO_M(init.annual_value_low ?? 0),
          high: RAW_TO_M(init.annual_value_high ?? 0),
        },
      }));

    return {
      launch: phaseToOpps(roadmap.phases.launch_now.initiatives, 'Low'),
      pilot: phaseToOpps(roadmap.phases.pilot.initiatives, 'Medium'),
      monitor: phaseToOpps(roadmap.phases.scale.initiatives, 'Medium'),
      defer: phaseToOpps(roadmap.phases.defer.initiatives, 'High'),
    };
  }, [piReport]);

  // Handlers
  const handleToggleFinalist = async (id: string) => {
    if (!clientId) return;
    const ucId = parseInt(id, 10);
    if (isNaN(ucId)) return;

    const wasSelected = finalistIds.has(id);
    // Persist via EAContext (local cache + evaluate-state DB) and selection API below
    if (wasSelected) {
      removeFinalist(id);
      setFinalistSnapshots((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        saveFinalistSnapshots(next);
        return next;
      });
    } else {
      addFinalist(id);
      // Cache the row we just selected so Executive Summary can show it immediately
      // even after leaving this explorer page.
      const fromExplorer = apiExplorerOpps.find((o) => o.id === id);
      const fromReport = (apiOpportunities ?? []).find((o) => o.id === id);
      const snap = fromExplorer
        ? toReportOpportunitySnapshot(fromExplorer)
        : fromReport
          ? fromReport
          : null;
      if (snap) {
        setFinalistSnapshots((prev) => {
          const next = { ...prev, [id]: snap };
          saveFinalistSnapshots(next);
          return next;
        });
      }
    }

    try {
      if (wasSelected) {
        await deselectUseCase(String(clientId), ucId);
      } else {
        await selectUseCase(String(clientId), ucId);
      }
      setSelectionSyncError(null);
    } catch (err) {
      // Do not roll back UI — EAContext / evaluate-state remains the durable source of truth.
      const message = err instanceof Error ? err.message : 'Selection update failed';
      console.warn('[UCE Selection] Backend update failed (evaluate-state kept):', message);
      setSelectionSyncError(message);
    }
  };

  const handleClearFilters = () => {
    setOrgFilter(ORG_FILTER_ALL);
    writeOrgFilterSession(ORG_FILTER_ALL);
    setCategoryFilter('all');
    setEffortFilter('all');
    setTimeFilter('all');
    setValueFilter('all');
    setCurrentPage(1);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= displayTotalPages) setCurrentPage(page);
  };

  const handleNavigate = (path: string, useCaseId?: string) => {
    const restoreState: RestoreState = {
      scrollY: window.scrollY,
      useCaseId: useCaseId || '',
    };

    // Hand the brief / ROI pages data Evaluate already has so they skip re-fetching
    // the single opportunity + 200-row catalog on open.
    const rankedIds = (uceCatalog.length > 0
      ? [...uceCatalog]
          .sort((a, b) => (a.rank ?? Number.POSITIVE_INFINITY) - (b.rank ?? Number.POSITIVE_INFINITY))
          .map((o) => String(o.use_case_id))
      : apiExplorerOpps.map((o) => o.id));

    let briefSnapshot: {
      id: string;
      title: string;
      category: string;
      totalScore?: number;
      valueBand?: string;
    } | undefined;
    if (useCaseId && path.includes('/brief')) {
      void fetchEvaluation(String(clientId), useCaseId);
    }

    if (useCaseId) {
      const fromPage = apiExplorerOpps.find((o) => o.id === useCaseId);
      if (fromPage) {
        briefSnapshot = {
          id: fromPage.id,
          title: fromPage.title,
          category: fromPage.category,
          totalScore: fromPage.totalScore,
          valueBand: fromPage.valueBand,
        };
      } else {
        const fromCatalog = uceCatalog.find((o) => String(o.use_case_id) === useCaseId);
        if (fromCatalog) {
          briefSnapshot = {
            id: String(fromCatalog.use_case_id),
            title: fromCatalog.name,
            category: fromCatalog.categoryFit?.trim() || fromCatalog.category_name || 'General',
            totalScore: fromCatalog.total_score,
            valueBand: String(fromCatalog.value_band ?? ''),
          };
        }
      }
    }

    navigate(path, {
      state: {
        restore: restoreState,
        from: location.pathname,
        returnTo: location.pathname + location.search,
        returnToTab: activeTab,
        ...(briefSnapshot ? { briefSnapshot } : {}),
        ...(rankedIds.length > 0
          ? { rankedIds, catalogTotal: uceCatalogTotal || rankedIds.length }
          : {}),
      },
    });
  };

  const handleNavigateToWeights = () => {
    navigate('/ea/weights', {
      state: {
        returnTo: location.pathname + location.search,
        restoreScrollY: window.scrollY,
        returnToTab: activeTab,
      }
    });
  };

  const handleBack = () => navigate('/ea/personalization');
  const handleContinue = () => setShowExecuteConfirm(true);
  const handleConfirmExecute = () => {
    setShowExecuteConfirm(false);
    navigate('/ea/activate');
  };

  const getShortlistLabel = (id: string): string | null => {
    const sid = String(id);
    const inBucket = (bucket: Set<string>) =>
      bucket.has(sid) || Array.from(bucket).some((existing) => String(existing) === sid);

    if (inBucket(initiativeShortlistBuckets.launch)) return SHORTLIST_BUCKET_LABELS.launch;
    if (inBucket(initiativeShortlistBuckets.pilot)) return SHORTLIST_BUCKET_LABELS.pilot;
    if (inBucket(initiativeShortlistBuckets.monitor)) return SHORTLIST_BUCKET_LABELS.monitor;
    if (inBucket(initiativeShortlistBuckets.defer)) return SHORTLIST_BUCKET_LABELS.defer;
    return null;
  };

  // Only count postures on initiatives actually going to Execute — not stale tags left
  // on use cases that were removed from the portfolio.
  const carryForwardShortlistCount = useMemo(() => {
    return reportSelectedOpps.filter((opp) => {
      const sid = String(opp.id);
      const inBucket = (bucket: Set<string>) =>
        bucket.has(sid) || Array.from(bucket).some((existing) => String(existing) === sid);
      return (
        inBucket(initiativeShortlistBuckets.launch)
        || inBucket(initiativeShortlistBuckets.pilot)
        || inBucket(initiativeShortlistBuckets.monitor)
        || inBucket(initiativeShortlistBuckets.defer)
      );
    }).length;
  }, [reportSelectedOpps, initiativeShortlistBuckets]);

  const handleTabChange = (tab: EvaluateTab) => {
    if (tab === activeTab) return;
    writeEvaluateTab(tab);
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  };

  const compareBriefs = useMemo(() => {
    return compare.compareIds
      .map((id) => {
        const fromPage = apiExplorerOpps.find((o) => o.id === id);
        if (fromPage) return toExplorerBrief(fromPage);
        const catalogIdx = uceCatalog.findIndex((o) => String(o.use_case_id) === id);
        if (catalogIdx >= 0) return uceToExplorerBrief(uceCatalog[catalogIdx], catalogIdx);
        return null;
      })
      .filter((b): b is ExplorerRowBrief => Boolean(b));
  }, [compare.compareIds, apiExplorerOpps, uceCatalog]);

  const showCompare = compare.compareMode && compareBriefs.length >= MIN_COMPARE;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">

      {/* Journey bar scrolls with the page; tab bar sticks below the main app header */}
      <div className="px-6 pt-2 print:hidden">
        <EAJourneyBar />
      </div>

      <div
        id="ea-opportunities-sticky-header"
        className="sticky top-[72px] z-10 bg-[#F5F7FA] px-6 pt-1 pb-1 border-b border-[#e5e7eb]/60 shadow-sm print:hidden"
      >
        <EATabBar
          activeTab={activeTab}
          onChange={handleTabChange}
          showAdjustWeights={activeTab === 'explore'}
          onAdjustWeights={handleNavigateToWeights}
        />
      </div>

      {/* ── Main Content ── */}
      <main className="px-6 py-5 pb-24">

        {/* ════════════════════════════════════════
            TAB 1: PORTFOLIO STRATEGY REPORT
            ════════════════════════════════════════ */}
        {activeTab === 'report' && (
          <>
            {piLoading && (
              <div className="flex items-center justify-center py-16">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-8 h-8 border-2 border-[#18365E]/20 border-t-[#18365E] rounded-full animate-spin" />
                  <p className="text-sm text-[#717182]">Loading portfolio strategy report…</p>
                </div>
              </div>
            )}
            {!piLoading && (
              <>
                {piError && !piReport ? (
                  <EAApiErrorPanel
                    title="Portfolio data unavailable"
                    message="We couldn't load the portfolio strategy report. Please check your connection and try again."
                    detail={piError}
                    onRetry={() => window.location.reload()}
                  />
                ) : piReport ? (
                  <OIRReportView
                    clientName={reportClientName}
                    rankedOpportunities={reportRankedOpps}
                    selectedOpportunities={reportSelectedOpps}
                    top10={reportTop10}
                    top3Opps={apiTop3Opps}
                    portfolioRangeLabel={reportPortfolioRangeLabel}
                    shortlistedRangeLabel={reportShortlistedLabel}
                    apiWaterfall={reportWaterfall}
                    avgTimeToValue={piReport?.sections?.financial_impact?.avg_time_to_value}
                    finalistCount={reportFinalistCount}
                    totalOpportunities={reportTotalCount}
                    personalizationData={personalizationData}
                    strategicPriorities={piReport?.sections?.strategic_context?.strategic_priorities}
                    knownConstraints={piReport?.sections?.strategic_context?.key_constraints}
                    apiRoadmapBuckets={apiRoadmapBuckets}
                    onOpenExplorationTab={() => handleTabChange('explore')}
                    onContinueToExecute={handleContinue}
                    onNavigate={handleNavigate}
                    onBack={handleBack}
                  />
                ) : null}
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════
            TAB 2: USE CASE EXPLORER
            ════════════════════════════════════════ */}
        {activeTab === 'explore' && showCompare && (
          <>
            <div className="mb-4 flex items-center gap-3">
              <button
                type="button"
                onClick={compare.exitCompare}
                className="flex items-center gap-1.5 text-xs font-semibold text-[#717182] hover:text-[#18365E] transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                All opportunities
              </button>
            </div>
            <h2 className="text-lg font-bold text-[#18365E] mb-1">Side-by-side comparison</h2>
            <p className="text-xs text-[#717182] mb-4">
              Comparing {compareBriefs.length} candidate{compareBriefs.length !== 1 ? 's' : ''}. Remove one here, or uncheck it in the list.
            </p>
            <ExplorerCompareTable
              briefs={compareBriefs}
              portfolioIds={Array.from(finalistIds)}
              onFocus={(id) => handleNavigate(`/ea/use-case/${id}/brief`, id)}
              onRemove={compare.remove}
            />
          </>
        )}

        {activeTab === 'explore' && !showCompare && (
          <>
            {/* ── API error banner (loading uses the single indicator above the table) ── */}
            {explorerError && !explorerLoading && !useApiExplorer && (
              <div className="mb-2 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                <span className="font-semibold">Unable to load opportunities.</span>
                <span className="text-red-600/80">{explorerError}</span>
              </div>
            )}

            {/* ── Row 1: Opportunity Portfolio framing line ── */}
            <div className="mb-3 flex items-center gap-3 flex-wrap">
              <span className="text-sm font-semibold text-[#18365E] shrink-0">Opportunity Portfolio</span>
              <span className="text-[11px] text-[#717182]">
                {explorerLoading && !useApiExplorer
                  ? 'Loading evaluated opportunities...'
                  : explorerError && !useApiExplorer
                    ? 'Data unavailable — please retry'
                    : activeFilterCount > 0
                      ? `${displayTotal} of ${explorerTotal} evaluated opportunities · ranked by strategic value and implementation feasibility`
                      : `${displayTotal} evaluated opportunities · ranked by strategic value and implementation feasibility`}
              </span>
              {lastComputedLabel && (
                <span
                  className="text-[11px] text-[#717182] whitespace-nowrap"
                  title={metricsLastComputedAt ? new Date(metricsLastComputedAt).toLocaleString() : undefined}
                >
                  · {lastComputedLabel}
                </span>
              )}
              {activeFilterCount > 0 && (
                <button
                  onClick={handleClearFilters}
                  className="text-[11px] font-semibold text-[#FF6E4C] hover:text-[#FF6E4C]/80 transition-colors whitespace-nowrap"
                >
                  Clear filters ×
                </button>
              )}

              {/* Portfolio status — pushed to right */}
              <div className="ml-auto flex items-center gap-2 shrink-0">
                {hasPortfolioInitiative ? (
                  <>
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-[#717182]">Portfolio:</span>
                      <span className="font-semibold text-[#18365E]">
                        {carryForwardCount} initiative{carryForwardCount !== 1 ? 's' : ''}
                      </span>
                      <span className="text-[#d1d5db]">|</span>
                      <span className="font-semibold text-[#18365E]">
                        {carryForwardValueLabel}
                      </span>
                      {!hasShortlistSelection && (
                        <>
                          <span className="text-[#d1d5db]">|</span>
                          <span className="text-[#717182]">Shortlist: 0</span>
                        </>
                      )}
                      {isPro && !hasPersonalizationComplete && personalizationBlockers.length > 0 && (
                        <>
                          <span className="text-[#d1d5db]">|</span>
                          <span className="text-[#717182]">
                            Personalize over {PERSONALIZATION_TAB_MIN_PERCENT}% needed
                          </span>
                        </>
                      )}
                    </div>
                    {showContinueToExecute && (
                      <button
                        onClick={handleContinue}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-colors whitespace-nowrap bg-[#FF6E4C] text-white hover:bg-[#FF6E4C]/90"
                      >
                        Continue to Execute
                        <ArrowRight className="w-3 h-3" />
                      </button>
                    )}
                  </>
                ) : (
                  <span className="text-[11px] text-[#717182]/50 italic whitespace-nowrap">
                    Portfolio: 0 selected · No portfolio value yet
                  </span>
                )}
              </div>
            </div>

            {/* Workbench Card */}
            {/* Single loader while data is already on screen (filter / pagination). */}
            {explorerLoading && displayOpps.length > 0 && (
              <div
                className="flex items-center justify-center gap-2 py-2.5 mb-3 text-xs font-semibold text-[#18365E] bg-[#F5F7FA] border border-[#e5e7eb] rounded-lg"
                role="status"
                aria-live="polite"
              >
                <div className="w-3.5 h-3.5 border-2 border-[#18365E]/20 border-t-[#18365E] rounded-full animate-spin" aria-hidden="true" />
                <span>Loading opportunities…</span>
              </div>
            )}
            <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden mb-6">

              {/* ── Row 2: Sort + Primary filters — always visible, Pro only ── */}
              {isPro && (
                <div className="px-4 py-2 border-b border-[#e5e7eb] bg-[#FAFBFC] flex items-center gap-3 flex-wrap">

                  {/* Sort */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Sort:</span>
                    <select
                      value={sortOption}
                      onChange={(e) => setSortOption(e.target.value as SortOption)}
                      className="text-[11px] font-medium text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      <option value="rank">Rank</option>
                      <option value="roi_high">Highest ROI</option>
                      <option value="time_to_value">Earliest First</option>
                      <option value="effort">Easiest First</option>
                      <option value="confidence">Confidence</option>
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Organization — DMD-1064: filter use_cases.tenant_id */}
                  <label
                    className="flex items-center gap-1.5 cursor-pointer select-none"
                    title="Show only use cases for this organization (use_cases.tenant_id). Uncheck to include the standard library."
                  >
                    <input
                      type="checkbox"
                      checked={orgFilterActive}
                      onChange={(e) => {
                        setOrgFilter(e.target.checked ? journeyOrg.id : ORG_FILTER_ALL);
                        setCurrentPage(1);
                      }}
                      className="w-3.5 h-3.5 rounded border-[#e5e7eb] text-[#18365E] focus:ring-[#18365E]/30 cursor-pointer"
                    />
                    <span className="text-[11px] font-medium text-[#18365E] whitespace-nowrap">
                      {journeyOrg.name}
                    </span>
                  </label>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Category */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Category:</label>
                    <select
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                      className="text-[11px] text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      {uniqueCategories.map(cat => (
                        <option key={cat} value={cat}>{cat === 'all' ? 'All' : cat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Effort */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Effort:</label>
                    <select
                      value={effortFilter}
                      onChange={(e) => setEffortFilter(e.target.value as EffortFilter)}
                      className="text-[11px] text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      <option value="all">All</option>
                      <option value="Low">Quick Win</option>
                      <option value="Medium">Moderate</option>
                      <option value="High">High</option>
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Timeline */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Timeline:</label>
                    <select
                      value={timeFilter}
                      onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                      className="text-[11px] text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      <option value="all">Any</option>
                      <option value="under6">Under 6 mo</option>
                      <option value="6to12">6–12 mo</option>
                      <option value="over12">Over 12 mo</option>
                    </select>
                  </div>

                  <div className="w-px h-4 bg-[#e5e7eb] shrink-0" />

                  {/* Value */}
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] font-medium text-[#717182] uppercase tracking-wide whitespace-nowrap">Value:</label>
                    <select
                      value={valueFilter}
                      onChange={(e) => setValueFilter(e.target.value as ValueFilter)}
                      className="text-[11px] text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20 cursor-pointer"
                    >
                      <option value="all">Any</option>
                      <option value="under2">Under $2M</option>
                      <option value="2to5">$2M–$5M</option>
                      <option value="over5">Over $5M</option>
                    </select>
                  </div>
                </div>
              )}



              {/* Column headers — widths mirror ExplorerRow so each label sits above its own column. */}
              {displayOpps.length > 0 && (
                <div className="px-5 py-2.5 bg-[#F5F7FA] border-b border-[#e5e7eb] flex items-center gap-4 text-[10px] font-semibold text-[#717182] uppercase tracking-wide">
                  <span className="w-9 shrink-0 text-center">Rank</span>
                  <span className="flex-1 min-w-0 text-center">Opportunity · Executive verdict</span>
                  <span className="hidden sm:block w-28 shrink-0 text-right">Est. value</span>
                  <span className="hidden md:block w-24 shrink-0">Time to value</span>
                  <span className="hidden lg:block w-20 shrink-0">Effort</span>
                  <span className="w-56 shrink-0 text-center">Compare · Open</span>
                </div>
              )}

              {/* Initiative rows */}
              <div>
                {/* DMD-1167: Show centered full-area spinner on the first load
                    whenever there is no data yet, regardless of useApiExplorer. */}
                {explorerLoading && displayOpps.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <div className="w-8 h-8 border-2 border-[#18365E]/20 border-t-[#18365E] rounded-full animate-spin mx-auto mb-3" />
                    <p className="text-sm font-medium text-[#18365E]">Loading opportunities...</p>
                    <p className="text-xs text-[#717182] mt-1">Fetching ranked opportunities from API</p>
                  </div>
                ) : displayOpps.length === 0 ? (
                  explorerError && !explorerLoading ? (
                    <div className="px-5 py-14 text-center">
                      <div className="w-12 h-12 rounded-full bg-[#F5F7FA] border border-[#e5e7eb] flex items-center justify-center mx-auto mb-3">
                        <AlertTriangle className="w-6 h-6 text-[#717182]" />
                      </div>
                      <p className="text-sm font-semibold text-[#18365E] mb-1">No data available</p>
                      <p className="text-xs text-[#717182] max-w-xs mx-auto">
                        We couldn't retrieve opportunity data from the server. Please try again later or contact support if the issue persists.
                      </p>
                      <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-3 py-1.5 text-xs font-semibold text-white bg-[#18365E] rounded-lg hover:bg-[#18365E]/90 transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  ) : (
                    <div className="px-5 py-12 text-center">
                      <Filter className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
                      <p className="text-sm font-medium text-[#18365E]">No opportunities match your filters</p>
                      <p className="text-xs text-[#717182] mt-1">Try adjusting your filter criteria</p>
                      <button onClick={handleClearFilters} className="mt-3 text-xs font-medium text-[#FF6E4C] hover:text-[#FF6E4C]/80">
                        Clear all filters
                      </button>
                    </div>
                  )
                ) : (
                  displayOpps.map((opportunity) => {
                    const isFinalist = finalistIds.has(String(opportunity.id));
                    const isCompared = compare.compareIds.includes(opportunity.id);
                    return (
                      <ExplorerRow
                        key={opportunity.id}
                        brief={toExplorerBrief(opportunity)}
                        highlight={isPro && isFinalist}
                        compareChecked={isCompared}
                        compareDisabled={!isCompared && compare.isFull}
                        onToggleCompare={() => compare.toggle(opportunity.id)}
                        onOpen={() => handleNavigate(`/ea/use-case/${opportunity.id}/brief`, opportunity.id)}
                        onPrefetch={() => { void fetchEvaluation(String(clientId), opportunity.id); }}
                        surfacedReasons={opportunity.surfacedReasons}
                        isInPortfolio={isFinalist}
                        portfolioLocked={isFreemium}
                        onTogglePortfolio={() => {
                          if (isFreemium) {
                            navigate('/ea/upgrade');
                          } else {
                            handleToggleFinalist(opportunity.id);
                          }
                        }}
                      />
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              {isPro && displayTotalPages > 1 && (
                <div className="px-5 py-2.5 border-t border-[#e5e7eb] bg-[#F5F7FA]/50">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="text-xs text-[#717182] flex items-center gap-2">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, displayTotal)} of {displayTotal} opportunities
                    </span>
                    <div className="flex items-center gap-1">
                      {/* DMD-1167: pagination buttons disabled while loading */}
                      <button
                        onClick={() => goToPage(1)}
                        disabled={currentPage === 1 || explorerLoading}
                        className={`p-1.5 rounded transition-colors ${currentPage === 1 || explorerLoading ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}
                      >
                        <ChevronsLeft className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1 || explorerLoading}
                        className={`p-1.5 rounded transition-colors ${currentPage === 1 || explorerLoading ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>

                      <div className="flex items-center gap-0.5 mx-1">
                        {(() => {
                          const pages: (number | '...')[] = [];
                          if (displayTotalPages <= 7) {
                            for (let i = 1; i <= displayTotalPages; i++) pages.push(i);
                          } else if (currentPage <= 4) {
                            pages.push(1, 2, 3, 4, 5, '...', displayTotalPages);
                          } else if (currentPage >= displayTotalPages - 3) {
                            pages.push(1, '...', displayTotalPages - 4, displayTotalPages - 3, displayTotalPages - 2, displayTotalPages - 1, displayTotalPages);
                          } else {
                            pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', displayTotalPages);
                          }
                          return pages.map((p, i) =>
                            p === '...' ? (
                              <span key={`ellipsis-${i}`} className="w-7 text-center text-xs text-[#717182]">...</span>
                            ) : (
                              <button
                                key={p}
                                onClick={() => goToPage(p as number)}
                                disabled={explorerLoading}
                                className={`w-7 h-7 rounded text-xs font-medium transition-colors ${currentPage === p ? 'bg-[#18365E] text-white' : 'text-[#717182] hover:bg-white hover:text-[#18365E] disabled:opacity-50 disabled:cursor-not-allowed'}`}
                              >
                                {p}
                              </button>
                            )
                          );
                        })()}
                      </div>

                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === displayTotalPages || explorerLoading}
                        className={`p-1.5 rounded transition-colors ${currentPage === displayTotalPages || explorerLoading ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => goToPage(displayTotalPages)}
                        disabled={currentPage === displayTotalPages || explorerLoading}
                        className={`p-1.5 rounded transition-colors ${currentPage === displayTotalPages || explorerLoading ? 'text-[#e5e7eb] cursor-not-allowed' : 'text-[#717182] hover:text-[#18365E] hover:bg-white'}`}
                      >
                        <ChevronsRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>

            {/* Upgrade Teaser (Freemium Only) */}
            {isFreemium && (
              <div className="mb-6 p-5 bg-white border border-[#e5e7eb] rounded-xl">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#18365E]/10 flex items-center justify-center shrink-0">
                    <Zap className="w-5 h-5 text-[#18365E]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-[#18365E]">More opportunities available</h3>
                    <p className="text-xs text-[#717182] mt-1 leading-relaxed">
                      Upgrade to Pro to view the full ranked list, apply filters, and compare additional opportunities.
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/ea/upgrade')}
                    className="px-4 py-2 bg-[#FF6E4C] text-white rounded-lg text-xs font-semibold hover:bg-[#FF6E4C]/90 transition-colors shrink-0"
                  >
                    Upgrade to Pro
                  </button>
                </div>
              </div>
            )}

            {/* Footer nav */}
            <div className="pt-4 border-t border-[#e5e7eb] flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 px-4 py-2 text-[#717182] hover:text-[#18365E] hover:bg-white rounded-lg transition-all text-sm font-medium"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Personalize
                </button>
                {Boolean((import.meta as any)?.env?.DEV) && (
                  <button
                    onClick={() => {
                      localStorage.setItem('dva_demo_plan_override', 'freemium');
                      window.location.reload();
                    }}
                    className="text-[10px] text-[#717182]/50 hover:text-[#717182] transition-colors"
                  >
                    Reset Demo
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3">
                {isPro && !hasPortfolioInitiative && (
                  <span className="text-xs text-[#717182]">
                    Add at least one initiative to your portfolio to continue
                  </span>
                )}
                {showContinueToExecute && (
                  <button
                    onClick={handleContinue}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all bg-[#FF6E4C] text-white hover:bg-[#FF6E4C]/90 shadow-md"
                  >
                    Continue to Execute
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}

      </main>

      {activeTab === 'explore' && !showCompare && (
        <div className="print:hidden">
          <CompareTray
            count={compare.compareIds.length}
            onCompare={compare.enterCompare}
            onClear={compare.clear}
          />
        </div>
      )}

      <ExecuteCarryForwardDialog
        open={showExecuteConfirm}
        onOpenChange={setShowExecuteConfirm}
        onConfirm={handleConfirmExecute}
        initiatives={reportSelectedOpps}
        portfolioCount={carryForwardCount}
        portfolioValueLabel={carryForwardValueLabel}
        shortlistCount={carryForwardShortlistCount}
        getShortlistLabel={getShortlistLabel}
      />

      {/* Chat FAB */}
      <button
        onClick={() => setChatOpen(!chatOpen)}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg transition-all flex items-center justify-center z-50 ${chatOpen ? 'bg-[#717182]' : 'bg-[#18365E] hover:bg-[#18365E]/90'
          } text-white`}
      >
        {chatOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {/* Chat Panel */}
      {chatOpen && (
        <div className="fixed bottom-24 right-6 w-80 h-96 bg-white rounded-xl shadow-2xl border border-[#e5e7eb] z-50 flex flex-col overflow-hidden">
          <div className="p-3 border-b bg-[#18365E]">
            <p className="text-[10px] text-white/70 font-medium uppercase tracking-wide">DVA Assistant</p>
            <p className="text-sm font-semibold text-white">Evaluation Help</p>
          </div>
          <div className="flex-1 p-4 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-10 h-10 text-[#e5e7eb] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#18365E]">Coming Soon</p>
              <p className="text-xs text-[#717182] mt-1 max-w-[200px]">
                AI-powered help to guide your evaluation and selection process.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExecuteCarryForwardDialog({
  open,
  onOpenChange,
  onConfirm,
  initiatives,
  portfolioCount,
  portfolioValueLabel,
  shortlistCount,
  getShortlistLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  initiatives: ReportOpportunity[];
  portfolioCount: number;
  portfolioValueLabel: string;
  shortlistCount: number;
  getShortlistLabel: (id: string) => string | null;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 pr-12">
          <DialogTitle className="text-base text-[#18365E]">
            Carry this portfolio to Execute?
          </DialogTitle>
          <DialogDescription className="text-xs text-[#717182]">
            Everything in this list is added to your portfolio and will go to Execute. Shortlist assignments are posture only — they do not drop initiatives from the handoff.
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 pb-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-[#18365E]/5 px-2.5 py-1 text-[11px] font-semibold text-[#18365E]">
            {portfolioCount} going to Execute
          </span>
          <span className="rounded-full bg-[#18365E]/5 px-2.5 py-1 text-[11px] font-semibold text-[#18365E]">
            {portfolioValueLabel}
          </span>
          {shortlistCount > 0 && (
            <span className="rounded-full bg-[#FFF4F1] px-2.5 py-1 text-[11px] font-semibold text-[#FF6E4C]">
              {shortlistCount} with shortlist posture
            </span>
          )}
        </div>

        <div className="mx-5 mb-4 max-h-[42vh] overflow-y-auto rounded-lg border border-[#e5e7eb] bg-[#FAFBFC]">
          {initiatives.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-[#717182]">
              Portfolio details are still loading. You can continue with {portfolioCount} selected initiative{portfolioCount !== 1 ? 's' : ''}.
            </p>
          ) : (
            <ul className="divide-y divide-[#e5e7eb]">
              {initiatives.map((initiative, index) => {
                const shortlistLabel = getShortlistLabel(initiative.id);
                return (
                  <li key={initiative.id} className="px-3 py-2.5 bg-white">
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 w-5 shrink-0 text-[10px] font-bold text-[#717182]">
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-[#18365E] leading-snug">
                          {initiative.title}
                        </p>
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#717182]">
                          <span className="font-semibold text-[#18365E]">
                            {(initiative.roiEstimate.low || 0) > 0 || (initiative.roiEstimate.high || 0) > 0
                              ? formatRoiRange(initiative.roiEstimate.low, initiative.roiEstimate.high)
                              : 'Value pending'}
                          </span>
                          {initiative.timeToValue !== '—' && (
                            <>
                              <span>{initiative.implementationEffort} effort</span>
                              <span>{initiative.timeToValue}</span>
                            </>
                          )}
                          {shortlistLabel && (
                            <span className="rounded-full bg-[#18365E]/5 px-1.5 py-0.5 font-semibold text-[#18365E]">
                              {shortlistLabel}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter className="px-5 py-4 border-t border-[#e5e7eb] bg-[#FAFBFC] sm:justify-end">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-[#18365E] bg-white border border-[#e5e7eb] hover:bg-[#F5F7FA] transition-colors"
          >
            Stay in Evaluate
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold text-white bg-[#FF6E4C] hover:bg-[#FF6E4C]/90 transition-colors"
          >
            Continue to Execute
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}